import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  ATTEMPT_DERIVED_STATUS,
  ATTEMPT_STATUSES,
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS,
  WORK_ORDER_CARD_STATUSES,
  WORK_ORDER_CARD_STATUS_CONFIG,
  WORK_ORDER_TOOLTIP_STATUSES,
} from "@miplatform/constants";
import {
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IWorkOrder,
  InspectionActionTypes,
} from "@miplatform/types";
import { mdiCalendarAccountOutline, mdiCheckCircle, mdiMinus } from "@mdi/js";
import { ButtonProps, Theme, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";
import { MiPopper } from "../MiPopper/MiPopper";
import { MiTemplateBadge } from "../MiTemplateBadge/MiTemplateBadge";
import { MiWorkOrderTooltip } from "./MiWorkOrderTooltip";

const convertTimeToShortHand = (time: string) => {
  const date = new Date(time);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${month}/${day} @ ${formattedHours}:${formattedMinutes}${ampm}`;
};

const getCardStatus = ({
  attemptStatus,
  derivedStatus,
  isBehind,
  isLate,
  isPreviousHold,
  isUnableToAutoAssign,
}: Partial<IAttempt> & {
  isBehind?: boolean;
  isLate?: boolean;
  isPreviousHold?: boolean;
  isUnableToAutoAssign?: boolean;
}) => {
  if (isUnableToAutoAssign) {
    return WORK_ORDER_CARD_STATUSES.UNABLE_TO_AUTO_ASSIGN;
  }

  if (attemptStatus === ATTEMPT_STATUSES.NEW) {
    return WORK_ORDER_CARD_STATUSES.NEW;
  }

  if (derivedStatus === ATTEMPT_DERIVED_STATUS.JOB_COMPLETE) {
    return WORK_ORDER_CARD_STATUSES.COMPLETE;
  }

  if (isLate && derivedStatus !== ATTEMPT_DERIVED_STATUS.JOB_COMPLETE) {
    return WORK_ORDER_CARD_STATUSES.LATE;
  }

  if (isBehind && isPreviousHold) {
    return WORK_ORDER_CARD_STATUSES.PREVIOUS_HOLD_BEHIND;
  }

  if (isPreviousHold) {
    return WORK_ORDER_CARD_STATUSES.HOLD_PREVIOUS_DAY;
  }

  if (attemptStatus === ATTEMPT_STATUSES.HOLD) {
    return WORK_ORDER_CARD_STATUSES.HOLD;
  }

  return WORK_ORDER_CARD_STATUSES.NEW;
};

const getTooltipStatus = ({
  attemptStatus,
  scheduledDate,
  isLate,
  isComplete,
}: {
  attemptStatus?: string;
  scheduledDate?: string;
  isLate?: boolean;
  isComplete?: boolean;
}): string => {
  if (attemptStatus === ATTEMPT_STATUSES.HOLD) {
    return WORK_ORDER_TOOLTIP_STATUSES.ON_HOLD;
  }
  if (isLate) {
    return WORK_ORDER_TOOLTIP_STATUSES.PAST_DUE;
  }
  if (isComplete) {
    return WORK_ORDER_TOOLTIP_STATUSES.COMPLETE;
  }
  if (scheduledDate) {
    return WORK_ORDER_TOOLTIP_STATUSES.SCHEDULED;
  }
  return WORK_ORDER_TOOLTIP_STATUSES.NOT_SCHEDULED;
};

const getBorderColor = (
  isLate: boolean,
  isBehind: boolean,
  theme: Theme
): string => {
  if (isLate) {
    return theme.palette.error.main;
  }
  if (isBehind) {
    return theme.palette.warning.main;
  }
  return theme.palette.grey[400];
};

export const MiWorkOrderCard = ({
  attempt,
  minimized = false,
  className,
  loading = false,
  onEventRemove,
  onCardClick,
  isRemovable = false,
}: MiWorkOrderCardProps) => {
  const theme = useTheme();
  const [popperOpen, setPopperOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    home,
    derivedStatus,
    attemptStatus,
    assignment,
    dueDate,
    completedSteps = 0,
    totalSteps = 0,
    startDate,
    endDate,
    scheduledStartDate,
    orderStatus,
    previousStatus,
    // @ts-expect-error - This field is not currently returned from the API, but will be implemented with auto-trigger
    isUnableToAutoAssign,
  } = attempt ?? {};
  const { workOrder } = assignment ?? {};
  const { workOrderTemplateColor, abbreviation, workOrderTemplateName } =
    workOrder ?? {};
  const { address, builderName, builderShortName, communityName } = home ?? {};
  const { addressLine1, city, regionCode } = address ?? {};

  const currentTime = new Date();

  const isPreviousHold = previousStatus === ATTEMPT_STATUSES.HOLD;
  const isLate = orderStatus === WORK_ORDER_CARD_STATUSES.LATE; // This will be implemented with auto-trigger

  const isScheduledStartDateBehind = scheduledStartDate
    ? new Date(scheduledStartDate) < currentTime
    : false;

  const isStartDateBehind =
    startDate && scheduledStartDate
      ? new Date(scheduledStartDate) < new Date(startDate)
      : false;

  const isBehind = isScheduledStartDateBehind || isStartDateBehind;

  const status = getCardStatus({
    attemptStatus,
    derivedStatus,
    endDate,
    startDate,
    isBehind,
    isLate,
    isPreviousHold,
    isUnableToAutoAssign,
  });

  const tooltipStatus = getTooltipStatus({
    attemptStatus,
    scheduledDate: scheduledStartDate,
    isLate,
    isComplete: derivedStatus === ATTEMPT_DERIVED_STATUS.JOB_COMPLETE,
  });

  const {
    headerColor = theme.palette.grey.accent,
    backgroundColor = theme.palette.common.white,
    textColor,
  } = WORK_ORDER_CARD_STATUS_CONFIG[status]
    ? WORK_ORDER_CARD_STATUS_CONFIG[status]
    : {};

  const borderColor = getBorderColor(isLate, isBehind, theme);

  const { classes, cx } = useStyles({
    borderColor,
    headerColor: minimized ? "transparent" : headerColor,
    backgroundColor,
    minimized,
    loading,
  });

  const color = textColor ?? theme.palette.text.primary;

  const openPopper = (e: React.MouseEvent<HTMLDivElement>) => {
    const { currentTarget } = e;
    setPopperOpen(true);
    setAnchorEl(currentTarget);
  };

  const closePopper = () => {
    setAnchorEl(null);
    setPopperOpen(false);
  };

  const badgeText = abbreviation ?? workOrderTemplateName?.slice(0, 3) ?? null;

  const handleClick = () => {
    onCardClick?.(attempt);
  };

  return (
    <Box
      className={cx(classes.card, className)}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
      onClick={handleClick}
    >
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          {badgeText && (
            <MiTemplateBadge
              title={badgeText}
              backgroundColor={workOrderTemplateColor}
            />
          )}
          <Typography fontSize={9} noWrap>
            {communityName}
          </Typography>
        </Box>
        <Box className={classes.headerRight}>
          <Typography
            fontSize={9}
            fontWeight={theme.fontWeight.semiBold}
            color={theme.palette.grey.medium}
            fontStyle="italic"
            pr={0.25}
            noWrap
          >
            {builderShortName}
          </Typography>
          {isRemovable && (
            <MiButton
              aria-label="Remove"
              size={BUTTON_SIZES.SMALL as ButtonProps["size"]}
              variant={BUTTON_VARIANTS.CONTAINED}
              color={BUTTON_COLORS.ERROR}
              className={classes.removeButton}
              startIcon={{
                path: mdiMinus,
                iconSize: 0.45,
                iconColor: theme.palette.common.white,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEventRemove?.();
              }}
            />
          )}
        </Box>
      </Box>
      {!minimized && (
        <Box className={classes.body}>
          <Box className={classes.row}>
            <Typography
              fontSize={9}
              color={color}
              textOverflow="ellipsis"
              noWrap
            >
              {addressLine1}
            </Typography>
            <Typography
              fontSize={9}
              color={color ?? theme.palette.grey.medium}
              fontStyle="italic"
              pr={0.25}
            >
              {city}
            </Typography>
          </Box>
          <Box className={classes.row}>
            <Box className={classes.row} alignItems="center">
              {totalSteps > 0 && (
                <>
                  {completedSteps === totalSteps && (
                    <MiIcon
                      path={mdiCheckCircle}
                      size={0.5}
                      color={theme.palette.success.main}
                    />
                  )}
                  <Typography
                    fontSize={9}
                    color={color}
                  >{`${completedSteps}/${totalSteps}`}</Typography>
                </>
              )}
            </Box>
            <Box className={classes.row}>
              {dueDate && (
                <Typography
                  fontSize={9}
                  color={isLate ? theme.palette.error.main : color}
                >
                  Due: {convertTimeToShortHand(dueDate)}
                </Typography>
              )}
              {isUnableToAutoAssign && (
                <MiIcon
                  path={mdiCalendarAccountOutline}
                  color={theme.palette.warning.main}
                  size={0.5}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
      <MiPopper
        anchorEl={anchorEl}
        arrowColor={headerColor}
        showArrow
        open={popperOpen}
      >
        <MiWorkOrderTooltip
          addressLine1={addressLine1}
          city={city}
          regionCode={regionCode}
          builderName={builderName}
          communityName={communityName}
          scheduledDate={scheduledStartDate}
          dueDate={dueDate}
          completeDate={endDate}
          status={tooltipStatus}
          templateName={workOrderTemplateName}
        />
      </MiPopper>
    </Box>
  );
};

interface MiWorkOrderCardProps {
  attempt: IAttemptListItemResponseForWorkOrder;
  minimized?: boolean;
  className?: string;
  loading?: boolean;
  onCardClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  onEventRemove?: () => void;
  isRemovable?: boolean;
}

const useStyles = makeStyles<IWorkOrderCardStyleProps>()(
  (theme, { borderColor, headerColor, backgroundColor, loading }) => {
    return {
      card: {
        border: `1px solid ${borderColor}`,
        borderRadius: 4,
        backgroundColor,
        overflow: "hidden",
        height: "100%",
        width: 160,
        "&:hover": {
          cursor: "grab",
        },
        "&:active": { cursor: "grabbing", opacity: 0.5 },
        opacity: loading ? 0.5 : 1,
        pointerEvents: loading ? "none" : "auto",
      },
      header: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        padding: theme.spacing(0.5),
        backgroundColor: headerColor,
        height: theme.spacing(4),
      },
      body: {
        padding: theme.spacing(0.5),
      },
      row: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
      },
      headerLeft: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1,
      },
      headerRight: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: theme.spacing(0.25),
        flexShrink: 0,
        minWidth: 0,
      },
      removeButton: {
        "&&": {
          flexShrink: 0,
          width: 14,
          height: 14,
          minWidth: 14,
          minHeight: 14,
          padding: 0,
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: theme.palette.error.dark,
          },
          "& .MuiButton-startIcon": {
            margin: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        },
      },
    };
  }
);

export interface IWorkOrderMenuAction {
  workOrder: IWorkOrder;
  actionType?: InspectionActionTypes | null;
}

export interface IWorkOrderCardStyleProps {
  borderColor?: string;
  headerColor?: string;
  backgroundColor?: string;
  minimized?: boolean;
  loading?: boolean;
}
